import type {ReactElement} from 'react';
import {resolveChatMediaUrl} from '@Libraries/ai-chat.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {useAiChatActions} from '@/ai-chat/providers/ai-chat.context';
import type {EntityCardProps} from './entity-card.types';
import {getEntityCardAction} from './actions/entity-card-action-registry';
import styles from './entity-card.module.scss';

const META_SEPARATOR = '  ·  ';

export function EntityCard({title, meta, mediaUrl, icon, entityType, entity}: EntityCardProps): ReactElement {
  const actions = useAiChatActions();

  const renderMedia = (): ReactElement | null => {
    if (mediaUrl !== undefined && mediaUrl !== '') {
      const resolvedMediaUrl = resolveChatMediaUrl(mediaUrl, MediaItemSizeType.THUMB);
      return (
        <div className={styles.media}>
          <img className={styles.mediaBackdrop} src={resolvedMediaUrl} alt="" aria-hidden="true" />
          <div className={styles.mediaBlur} aria-hidden="true" />
          <img className={styles.mediaImage} src={resolvedMediaUrl} alt={title ?? ''} />
        </div>
      );
    }

    if (icon !== undefined && icon !== '') {
      return (
        <div className={styles.iconThumb}>
          <i className={`${icon} ${styles.iconGlyph}`} aria-hidden="true" />
        </div>
      );
    }

    return null;
  };

  const renderTitle = (): ReactElement | null => {
    if (title === undefined || title === '') {
      return null;
    }
    return <Text val={title} size={TextSize.MD} bold className={styles.title} />;
  };

  const renderContent = (): ReactElement => {
    return (
      <>
        {renderMedia()}
        <div className={styles.text}>
          {renderTitle()}
          <Text val={meta.join(META_SEPARATOR)} size={TextSize.MD} className={styles.meta} />
        </div>
      </>
    );
  };

  const action = getEntityCardAction(entityType);

  if (action === undefined || entity === undefined) {
    return <div className={styles.root}>{renderContent()}</div>;
  }

  const handleClick = (): void => {
    void action.run(entity, {openPdf: actions.openPdf});
  };

  return (
    <ClickableDiv className={`${styles.root} ${styles.clickable}`} onClick={handleClick}>
      {renderContent()}
    </ClickableDiv>
  );
}
